import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const inputVariants = cva(
  "border-input border outline-none flex w-full min-w-0 flex-col items-start justify-center bg-transparent shadow-xs file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium selection:bg-primary selection:text-primary-foreground dark:bg-input/30 transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-0.75 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      shape: {
        default: "rounded-lg",
        pill: "rounded-full",
      },
      size: {
        sm: "h-8 px-3 py-1 text-sm",
        md: "h-input-md px-4 py-1 text-input-md",
        lg: "h-16 px-4 py-1 text-base",
      },
    },
    defaultVariants: {
      shape: "default",
      size: "md",
    },
  },
);

/**
 * It refers to padding, heights, and font size
 */
export type InputSize = VariantProps<typeof inputVariants>["size"];

export type InputShape = VariantProps<typeof inputVariants>["shape"];

export interface InputProps extends Omit<
  React.ComponentProps<"input">,
  "size"
> {
  shape?: InputShape;
  size?: InputSize;
  /**
   * It refers to 'size' attribute of the input element
   */
  length?: number;
}

function Input({
  className,
  type,
  shape = "default",
  size = "md",
  length,
  ...props
}: InputProps) {
  return (
    <input
      type={type}
      size={length}
      data-slot="input"
      className={cn(inputVariants({ shape, size }), className)}
      {...props}
    />
  );
}

export { Input };
